import React, { useState, useRef } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const VehiclePhotoUpload = ({ 
  formData, 
  onFormChange, 
  errors = {} 
}) => {
  const [uploadProgress, setUploadProgress] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const requiredPhotos = [
    { id: 'front', label: 'Front View', icon: 'Camera', required: true },
    { id: 'rear', label: 'Rear View', icon: 'Camera', required: true },
    { id: 'driver-side', label: 'Driver Side', icon: 'Camera', required: true },
    { id: 'passenger-side', label: 'Passenger Side', icon: 'Camera', required: true },
    { id: 'interior-front', label: 'Interior Front', icon: 'Camera', required: true },
    { id: 'interior-rear', label: 'Interior Rear', icon: 'Camera', required: false },
    { id: 'dashboard', label: 'Dashboard', icon: 'Camera', required: true },
    { id: 'odometer', label: 'Odometer Reading', icon: 'Camera', required: true },
    { id: 'engine', label: 'Engine Bay', icon: 'Camera', required: false },
    { id: 'trunk', label: 'Trunk/Cargo Area', icon: 'Camera', required: false },
    { id: 'wheels', label: 'Wheels/Tires', icon: 'Camera', required: false },
    { id: 'damage', label: 'Any Damage Areas', icon: 'Camera', required: false }
  ];

  const handleFileSelect = (files, photoType = null) => {
    const fileArray = Array.from(files);
    
    fileArray.forEach((file, index) => {
      if (file.type.startsWith('image/')) {
        const fileId = `${Date.now()}-${index}`;
        
        // Simulate upload progress
        setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));
        
        const reader = new FileReader();
        reader.onload = (e) => {
          const newPhoto = {
            id: fileId,
            file: file,
            url: e.target.result,
            type: photoType || 'general',
            name: file.name,
            size: file.size,
            uploadedAt: new Date().toISOString()
          };
          
          const currentPhotos = formData.photos || [];
          onFormChange('photos', [...currentPhotos, newPhoto]);
          
          // Simulate upload progress
          let progress = 0;
          const interval = setInterval(() => {
            progress += Math.random() * 30;
            if (progress >= 100) {
              progress = 100;
              clearInterval(interval);
              setTimeout(() => {
                setUploadProgress(prev => {
                  const updated = { ...prev };
                  delete updated[fileId];
                  return updated;
                });
              }, 500);
            }
            setUploadProgress(prev => ({ ...prev, [fileId]: Math.min(progress, 100) }));
          }, 200);
        };
        
        reader.readAsDataURL(file);
      }
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    handleFileSelect(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const removePhoto = (photoId) => {
    const currentPhotos = formData.photos || [];
    const updatedPhotos = currentPhotos.filter(photo => photo.id !== photoId);
    onFormChange('photos', updatedPhotos);
  };

  const getPhotosForType = (type) => {
    return (formData.photos || []).filter(photo => photo.type === type);
  };

  const getRequiredPhotoStatus = () => {
    const requiredTypes = requiredPhotos.filter(p => p.required).map(p => p.id);
    const uploadedTypes = (formData.photos || []).map(p => p.type);
    const completed = requiredTypes.filter(type => uploadedTypes.includes(type));
    return { completed: completed.length, total: requiredTypes.length };
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const requiredStatus = getRequiredPhotoStatus();

  return (
    <div className="space-y-6">
      {/* Progress Summary */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Photo Upload Progress</h3>
          <span className="text-sm text-muted-foreground">
            {requiredStatus.completed}/{requiredStatus.total} required photos
          </span>
        </div>
        
        <div className="w-full bg-muted rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full transition-smooth"
            style={{ width: `${(requiredStatus.completed / requiredStatus.total) * 100}%` }}
          ></div>
        </div>
        
        {requiredStatus.completed === requiredStatus.total && (
          <div className="flex items-center space-x-2 mt-3 text-success">
            <Icon name="CheckCircle" size={16} />
            <span className="text-sm font-medium">All required photos uploaded!</span>
          </div>
        )}
      </div>

      {/* Upload Area */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Upload Vehicle Photos</h3>
        
        <div
          className={`
            border-2 border-dashed rounded-lg p-8 text-center transition-smooth cursor-pointer
            ${dragOver 
              ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/50'
            }
          `}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
        >
          <Icon name="Upload" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h4 className="text-lg font-medium text-foreground mb-2">
            Drop photos here or click to browse
          </h4>
          <p className="text-sm text-muted-foreground mb-4">
            Supports JPG, PNG, HEIC up to 10MB each
          </p>
          <Button variant="outline" iconName="Camera" iconPosition="left">
            Choose Photos
          </Button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={(e) => handleFileSelect(e.target.files)}
          className="hidden"
        />
      </div>

      {/* Required Photos Checklist */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Photo Requirements</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Take clear, well-lit photos from these angles for the best offer
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {requiredPhotos.map((photoType) => {
            const hasPhoto = getPhotosForType(photoType.id).length > 0;
            
            return (
              <div
                key={photoType.id}
                className={`
                  flex items-center space-x-3 p-3 rounded-lg border transition-smooth
                  ${hasPhoto 
                    ? 'border-success bg-success/5 text-success' 
                    : photoType.required 
                      ? 'border-border' :'border-border opacity-75'
                  }
                `}
              >
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center
                  ${hasPhoto ? 'bg-success text-success-foreground' : 'bg-muted text-muted-foreground'}
                `}>
                  {hasPhoto ? (
                    <Icon name="Check" size={16} />
                  ) : (
                    <Icon name={photoType.icon} size={16} />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">
                    {photoType.label}
                    {photoType.required && <span className="text-destructive ml-1">*</span>}
                  </p>
                  {hasPhoto && (
                    <p className="text-xs text-muted-foreground">
                      {getPhotosForType(photoType.id).length} photo(s)
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Uploaded Photos Grid */}
      {(formData.photos || []).length > 0 && (
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Uploaded Photos ({(formData.photos || []).length})
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {(formData.photos || []).map((photo) => (
              <div key={photo.id} className="relative group">
                <div className="aspect-square rounded-lg overflow-hidden border border-border">
                  <Image
                    src={photo.url}
                    alt={photo.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                {/* Upload Progress */}
                {uploadProgress[photo.id] !== undefined && (
                  <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                    <div className="text-center text-white">
                      <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                      <span className="text-xs">{Math.round(uploadProgress[photo.id])}%</span>
                    </div>
                  </div>
                )}
                
                {/* Remove Button */}
                <button
                  onClick={() => removePhoto(photo.id)}
                  className="absolute top-2 right-2 w-6 h-6 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-smooth"
                >
                  <Icon name="X" size={12} />
                </button>
                
                {/* Photo Info */}
                <div className="mt-2">
                  <p className="text-xs font-medium text-foreground truncate">
                    {photo.type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatFileSize(photo.size)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Camera Tips */}
      <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Camera" size={20} className="text-accent mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-foreground mb-1">Photo Tips</h4>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• Take photos in good lighting (natural daylight preferred)</li>
              <li>• Clean your vehicle before photographing</li>
              <li>• Include close-ups of any damage or wear</li>
              <li>• Ensure odometer reading is clearly visible</li>
              <li>• Take photos from multiple angles for each area</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehiclePhotoUpload;